import {FC, useCallback, useEffect, useState} from 'react'
import {ReservationTable} from './ReservationTable'
import {
  initialReservationFilter,
  IReservationFilterModel,
  IReservationModel,
  OFFICE_FILTER_TYPE,
  validationReservationFilter,
} from './ReservationModel'
import {showErrorToast} from '../../../shared/components/ToastUtils'
import {filterReservations} from './Api'
import {ToastContainer} from 'react-toastify'
import AsyncSearchInput from '../../../shared/components/AsyncSearchInput'
import {
  AsyncCarModel,
  AsyncCustomerModel,
  AsyncVehicleGroupModel,
} from '../../../shared/models/ApiModels'
import {
  branches,
  fetchAsyncCarData,
  fetchAsyncCustomerData,
  fetchAsyncVehicleGroupData,
} from '../../../shared/models/SharedApiLookups'
import {Form, Formik} from 'formik'
import TextInput from '../../../shared/components/TextInput'
import SelectInput from '../../../shared/components/SelectInput'
import {DATE_FILTER_TYPE, RESERVATION_STATUS} from '../../../shared/models/SharedConstantLookups'
import DateInput from '../../../shared/components/DateInput'
import {useLocation} from 'react-router-dom'
import MultiSelectCombobox from '../../../shared/components/MultiSelectCombobox'

export const ReservationListPage: FC = () => {
  const [reservations, setReservations] = useState<IReservationModel[]>([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [pageSize, setPageSize] = useState(10)
  const [filterValues, setFilterValues] =
    useState<IReservationFilterModel>(initialReservationFilter)
  const [filterOpen, setFilterOpen] = useState(true)
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const isOpen = params.get('open') === 'true' // boolean
  const isToday = params.get('today') === 'true' // boolean

  useEffect(() => {
    Promise.all([branches()]).then(([brn]) => {
      setBranchOptions(brn)
    })
  }, [])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        filter(filterValues, newPage, pageSize, sortBy, sortOrder)
      }
    },
    [totalPages, pageSize, sortBy, sortOrder, filterValues]
  )

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      setPageSize(newSize)
      filter(filterValues, currentPage, newSize, sortBy, sortOrder)
    },
    [currentPage, sortBy, sortOrder, filterValues]
  )


  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      filter(filterValues, currentPage, pageSize, column, order)
    },
    [currentPage, pageSize, filterValues]
  )

  const filter = useCallback(
    async (
      values: IReservationFilterModel,
      page: number = 0,
      size: number = 10,
      sort?: string,
      order?: 'asc' | 'desc'
    ) => {
      try {
        const res = await filterReservations(values, page, size, sort, order)
        const data = res.data.payload || res.data
        setReservations(data?.content ?? [])
        setTotalPages(data?.totalPages ?? 1)
      } catch (err: any) {
        if (err?.response?.status === 401) {
          window.location.href = '/login'
          return
        }
        console.error(err)
        showErrorToast('Rezervasyonlar yüklenirken bir hata oluştu')
      }
    },
    [pageSize, filterValues]
  )

  const handleFilter = async (values: IReservationFilterModel) => {
    filter(values, currentPage, pageSize, sortBy, sortOrder)
  }

  const initialValues = {
    ...initialReservationFilter,
    status: isOpen ? '0' : '',
    dateFilterType: isToday ? 0 : null,
    endDate: isToday
      ? new Date()
      : (() => {
          const d = new Date()
          d.setDate(d.getDate() + 365)
          return d
        })(),
  }

  return (
    <>
      <div className='card'>
        {/* Card Header - Filtre Bölümü */}
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <div className='d-flex align-items-center gap-2 my-1'>
              <i className='bi bi-funnel fs-1 text-primary'></i>
              <h3 className='fw-bold m-0'>Rezervasyon Filtreleri</h3>
            </div>
          </div>

          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFilterOpen((prev) => !prev)}
            >
              <i className={`ki-duotone ki-${filterOpen ? 'up' : 'down'} fs-2`}>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
              {filterOpen ? 'Gizle' : 'Göster'}
            </button>
          </div>
        </div>

        {/* Collapsible Filter Form */}
        <div className={`collapse ${filterOpen ? 'show' : ''}`}>
          <div className='card-body border-top pt-6'>
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={validationReservationFilter}
              onSubmit={(values) => handleFilter(values)}
            >
              {(formik) => (
                <Form id='reservationFilterForm' className='form'>
                  {/* Row 1 */}
                  <div className='row g-6 mb-6'>
                    <AsyncSearchInput<AsyncCustomerModel>
                      name='customer'
                      placeholder='Ad Soyad girin...'
                      fetchHandler={fetchAsyncCustomerData}
                      displayField={(item) => `${item.firstName} ${item.lastName}`}
                      onSelect={(item) => formik.setFieldValue('customerId', item.id)}
                      onClear={() => formik.setFieldValue('customerId', null)}
                      wrapperClassName='col-md-4'
                    />
                    <TextInput
                      name='reservationNo'
                      placeholder='Rez/Doc No'
                      wrapperClassName='col-md-2'
                    />
                    <AsyncSearchInput<AsyncCarModel>
                      name='car'
                      placeholder='Araç plaka girin...'
                      fetchHandler={fetchAsyncCarData}
                      displayField={(item) => `${item.plate} (${item.modelYear})`}
                      onSelect={(item) => formik.setFieldValue('carId', item.id)}
                      onClear={() => formik.setFieldValue('carId', null)}
                      wrapperClassName='col-md-3'
                    />
                    <SelectInput
                      name='status'
                      disabled={isOpen}
                      wrapperClassName='col-md-3'
                      options={RESERVATION_STATUS}
                    />
                  </div>

                  {/* Row 2 */}
                  <div className='row g-6 mb-6'>
                    <SelectInput
                      name='dateFilterType'
                      wrapperClassName='col-md-2'
                      options={DATE_FILTER_TYPE}
                      disabled={isToday}
                    />
                    <DateInput
                      name='startDate'
                      type='date'
                      wrapperClassName='col-md-2'
                      readOnly={isToday}
                    />
                    <DateInput
                      name='endDate'
                      type='date'
                      wrapperClassName='col-md-2'
                      readOnly={isToday}
                    />
                    <AsyncSearchInput<AsyncVehicleGroupModel>
                      name='vehicleGroup'
                      placeholder='Araç grubu girin...'
                      fetchHandler={fetchAsyncVehicleGroupData}
                      displayField={(item) => `${item.groupName}`}
                      onSelect={(item) => formik.setFieldValue('vehicleGroupId', item.id)}
                      onClear={() => formik.setFieldValue('vehicleGroupId', null)}
                      clearValue={{id: '', groupName: ''}}
                      wrapperClassName='col-md-6'
                    />
                  </div>

                  {/* Row 3 */}
                  <div className='row g-6 mb-6'>
                    <AsyncSearchInput<AsyncCustomerModel>
                      name='customerCompany'
                      placeholder='Cari bilgi girin...'
                      fetchHandler={fetchAsyncCustomerData}
                      displayField={(item) => `${item.firstName} ${item.lastName}`}
                      onSelect={(item) => formik.setFieldValue('customerCompanyId', item.id)}
                      onClear={() => formik.setFieldValue('customerCompanyId', null)}
                      wrapperClassName='col-md-6'
                    />
                    <SelectInput
                      name='officeFilterType'
                      wrapperClassName='col-md-2'
                      options={OFFICE_FILTER_TYPE}
                    /> 
                    <MultiSelectCombobox 
                      name='officeIdList'
                      placeholder='Ofis seçin...'
                      options={branchOptions} 
                      wrapperClassName='col-md-4'
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className='d-flex justify-content-end gap-3'>
                    <button
                      type='button'
                      className='btn btn-light btn-active-light-primary'
                      onClick={() => formik.resetForm()}
                    >
                      Temizle
                    </button>

                    <button type='submit' className='btn btn-primary' disabled={loading}>
                      {loading ? (
                        <span className='indicator-progress' style={{display: 'block'}}>
                          Lütfen bekleyin...
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      ) : (
                        <>
                          <i className='ki-duotone ki-magnifier fs-2'>
                            <span className='path1'></span>
                            <span className='path2'></span>
                          </i>
                          Filtrele
                        </>
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        {/* Card Body - Table */}
        <div className='card-body pt-0'>
          <ReservationTable
            data={reservations}
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={handlePageChange}
            onEdit={(r) => console.log('go to edit page')}
            onDelete={(r) => console.log('delete reservation')}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={loading}
          />
        </div>
      </div>

      <ToastContainer position='top-right' autoClose={5000} newestOnTop pauseOnHover />
    </>
  )
}
